import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const UserTable = ({
  users,
  sortConfig,
  onSort,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onEditUser,
  onDeleteUser,
  onToggleStatus
}) => {
  const handleSort = (key) => {
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    onSort?.({ key, direction });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString)?.toLocaleDateString('pt-BR', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      manager: 'bg-orange-100 text-orange-800 border-orange-200', 
      viewer: 'bg-green-100 text-green-800 border-green-200'
    };

    const roleLabels = {
      admin: 'Admin',
      manager: 'Gestor',
      viewer: 'Leitor'
    };

    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
        roleStyles?.[role] || 'bg-gray-100 text-gray-800 border-gray-200'
      )}>
        {roleLabels?.[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border',
        statusStyles?.[status] || 'bg-gray-100 text-gray-800 border-gray-200'
      )}>
        <div className={cn(
          'w-1.5 h-1.5 rounded-full mr-1.5',
          status === 'active' ? 'bg-green-500' : 'bg-gray-500'
        )}></div>
        {status === 'active' ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  const SortHeader = ({ children, sortKey, className = '' }) => (
    <th
      className={cn(
        'px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-accent/50 transition-colors',
        className
      )}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortConfig?.key === sortKey && (
          <svg
            className={cn(
              'w-3 h-3 transition-transform',
              sortConfig?.direction === 'desc' && 'rotate-180'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        )}
      </div>
    </th>
  );

  const isAllSelected = users?.length > 0 && selectedUsers?.length === users?.length;
  const isIndeterminate = selectedUsers?.length > 0 && selectedUsers?.length < users?.length;

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onCheckedChange={(checked) => onSelectAll?.(checked)}
                  aria-label="Select all users"
                />
              </th>
              <SortHeader sortKey="name">Nome</SortHeader>
              <SortHeader sortKey="email">E-mail</SortHeader>
              <SortHeader sortKey="role">Perfil</SortHeader>
              <SortHeader sortKey="status">Status</SortHeader>
              <SortHeader sortKey="lastLogin">Último acesso</SortHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {users?.map((user) => (
              <tr
                key={user?.id}
                className={cn(
                  'hover:bg-accent/50 transition-colors',
                  selectedUsers?.includes(user?.id) && 'bg-blue-50'
                )}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onCheckedChange={(checked) => onSelectUser?.(user?.id, checked)}
                    aria-label={`Selecionar ${user?.name}`}
                  />
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-foreground">
                        {user?.name}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{user?.email}</div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  {getRoleBadge(user?.role)}
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(user?.status)}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {formatDate(user?.lastLogin)}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditUser?.(user)}
                      iconName="Edit"
                      className="text-blue-600 hover:text-blue-700"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus?.(user)}
                      iconName={user?.status === 'active' ? 'UserX' : 'UserCheck'}
                      className={cn(
                        user?.status === 'active' ?'text-orange-600 hover:text-orange-700' :'text-green-600 hover:text-green-700'
                      )}
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteUser?.(user)}
                      iconName="Trash2"
                      className="text-red-600 hover:text-red-700"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users?.length === 0 && (
        <div className="text-center py-12">
          <div className="text-muted-foreground">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="text-lg font-medium text-foreground mb-2">Nenhum usuário encontrado</h3>
            <p>Tente ajustar sua busca ou filtros.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;